/**
 * Pointer/touch input → intent {@link Action}s (HANDOFF §3.4 / §4.1 / §6 Agent E).
 *
 * Translates raw pointer/touch/mouse gestures over the play surface into sim
 * actions: the two-stage swing (select club → drag to aim → release → tap POWER
 * → tap IMPACT) plus card taps are handled by the UI hand, not here. This module
 * EMITS actions only via the injected `dispatch`; it NEVER mutates sim state and
 * never draws. The launch point is always the FIXED tee box (read from state),
 * not the finger (§3.4).
 *
 * The gesture→Action mapping lives in PURE, exported, unit-tested helpers
 * ({@link aimAngleFromDrag}, {@link tapToSwingAction}); the DOM wiring around them
 * is intentionally thin.
 */
import type { Action, GameState, SwingPhase, Vec2 } from '../sim/types.ts';
import { boxForPower } from '../sim/constants.ts';

/**
 * Dependencies the host (main.ts, owned by the overseer) injects. `screenToField`
 * is provided by the renderer so a client (CSS-pixel) coordinate maps into the
 * sim's virtual 440×900 FIELD space — aim is computed there (§3.4).
 */
export interface ControlDeps {
  /** Emit a sim action. The host routes it into the reducer. */
  dispatch(action: Action): void;
  /** Read the latest game state (for the active tee + swing phase). */
  getState(): GameState;
  /** Map a client (CSS px) point to FIELD space. Provided by the renderer. */
  screenToField(clientX: number, clientY: number): Vec2;
}

/** Minimum drag length in FIELD units before a release counts as an aim (§3.4). */
const MIN_DRAG_FIELD = 12;

// ---------------------------------------------------------------------------
// PURE helpers (exported + unit-tested)
// ---------------------------------------------------------------------------

/**
 * Aim angle (radians) for a drag, measured FROM the fixed tee TOWARD the drag
 * point in FIELD space (§3.4):
 *
 *     angle = atan2(drag.y − tee.y, drag.x − tee.x)
 *
 * The launch is always the tee box, never the finger, so the direction is the
 * tee→finger vector. Angle is scale-invariant but is computed in field space for
 * correctness with the rest of the sim.
 */
export function aimAngleFromDrag(teeField: Vec2, dragField: Vec2): number {
  return Math.atan2(dragField.y - teeField.y, dragField.x - teeField.x);
}

/**
 * Map a tap during a swing to the lock action for the current phase (§3.4):
 *   - `backswing` → `LockBackswing`
 *   - `impact`    → `LockImpact`
 *   - anything else (idle / aim / firing) → `null` (the tap does nothing here).
 */
export function tapToSwingAction(swingPhase: SwingPhase): Action | null {
  if (swingPhase === 'backswing') return { type: 'LockBackswing' };
  if (swingPhase === 'impact') return { type: 'LockImpact' };
  return null;
}

/** Squared distance between two field points (avoids a sqrt for the drag gate). */
function dist2(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * The active tee position in FIELD space for the side currently mid-swing, or null if no
 * card is selected. The card's POWER picks its tee ({@link boxForPower}); the aim is
 * anchored there (§3.4).
 */
export function activeTeeField(state: GameState): Vec2 | null {
  const { swing } = state;
  if (swing.clubIdx === null) return null;
  const side = swing.side === 'you' ? state.you : state.rival;
  const club = side.offenseHand[swing.clubIdx];
  if (club === undefined) return null;
  return side.tees[boxForPower(club.power)].pos;
}

// ---------------------------------------------------------------------------
// DOM wiring (thin; verified by the overseer running the app)
// ---------------------------------------------------------------------------

/**
 * Attach pointer/touch/mouse listeners to the play surface and translate gestures
 * into actions per §3.4. Returns a detach function that removes every listener.
 *
 * Behaviour by swing phase (read fresh from `getState()` on every event):
 *   - `aim`:       pointerdown begins a drag; pointermove dispatches `Aim{angle}`
 *                  (angle from the fixed tee → finger, field space); pointerup
 *                  dispatches `StartSwing` if the drag passed the min-length gate.
 *   - `backswing`: a tap (pointerdown) dispatches `LockBackswing`.
 *   - `impact`:    a tap (pointerdown) dispatches `LockImpact`.
 *   - otherwise:   pointer events are ignored (card selection happens in the hand
 *                  UI, which dispatches PlayOffense/PlayDefense itself).
 *
 * Scroll/zoom is suppressed on the surface (touch-action handled in CSS + the
 * passive:false listeners call preventDefault) so a swing drag never pans.
 */
export function attachControls(target: HTMLElement, deps: ControlDeps): () => void {
  const { dispatch, getState, screenToField } = deps;

  // Per-gesture drag bookkeeping (UI-only; not sim state).
  let dragging = false;
  let dragStartField: Vec2 | null = null;
  let activePointerId: number | null = null;

  /** Resolve the client point of an event to FIELD space. */
  function toField(clientX: number, clientY: number): Vec2 {
    return screenToField(clientX, clientY);
  }

  function beginAimDrag(clientX: number, clientY: number, pointerId: number | null): void {
    // Record the touch-down point but DON'T aim yet: the aim line must only move
    // once the finger actually drags, otherwise a bare tap snaps it to the finger.
    dragging = true;
    activePointerId = pointerId;
    dragStartField = toField(clientX, clientY);
  }

  function moveAimDrag(clientX: number, clientY: number): void {
    if (!dragging) return;
    const tee = activeTeeField(getState());
    if (tee === null) return;
    const dragField = toField(clientX, clientY);
    dispatch({ type: 'Aim', angle: aimAngleFromDrag(tee, dragField) });
  }

  function endAimDrag(clientX: number, clientY: number): void {
    if (!dragging) return;
    dragging = false;
    activePointerId = null;
    const start = dragStartField;
    dragStartField = null;
    if (start === null) return;
    const dragField = toField(clientX, clientY);
    // Too small a drag → cancel the shot rather than fire a wild swing (§3.4).
    if (dist2(start, dragField) < MIN_DRAG_FIELD * MIN_DRAG_FIELD) return;
    dispatch({ type: 'StartSwing' });
  }

  /** A bare tap (no drag) during the meter phases locks the current stage (§3.4). */
  function handleTapLock(): boolean {
    const action = tapToSwingAction(getState().swing.phase);
    if (action === null) return false;
    dispatch(action);
    return true;
  }

  // --- Pointer Events (preferred; covers mouse + touch + pen) ----------------

  function onPointerDown(e: PointerEvent): void {
    const phase = getState().swing.phase;
    if (phase === 'backswing' || phase === 'impact') {
      // Mid-meter: this tap is a lock, not the start of an aim drag.
      if (handleTapLock()) e.preventDefault();
      return;
    }
    if (phase !== 'aim') return; // No club selected yet; ignore.
    beginAimDrag(e.clientX, e.clientY, e.pointerId);
    if (typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        // Capture is best-effort; window-level up still ends the drag.
      }
    }
    e.preventDefault();
  }

  function onPointerMove(e: PointerEvent): void {
    if (!dragging) return;
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    moveAimDrag(e.clientX, e.clientY);
    e.preventDefault();
  }

  function onPointerUp(e: PointerEvent): void {
    if (!dragging) return;
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    endAimDrag(e.clientX, e.clientY);
    e.preventDefault();
  }

  // --- Touch fallback (older mobile WebViews without Pointer Events) ---------

  function onTouchStart(e: TouchEvent): void {
    const t = e.changedTouches[0];
    if (t === undefined) return;
    const phase = getState().swing.phase;
    if (phase === 'backswing' || phase === 'impact') {
      if (handleTapLock()) e.preventDefault();
      return;
    }
    if (phase !== 'aim') return;
    beginAimDrag(t.clientX, t.clientY, null);
    e.preventDefault();
  }

  function onTouchMove(e: TouchEvent): void {
    if (!dragging) return;
    const t = e.changedTouches[0];
    if (t === undefined) return;
    moveAimDrag(t.clientX, t.clientY);
    e.preventDefault();
  }

  function onTouchEnd(e: TouchEvent): void {
    if (!dragging) return;
    const t = e.changedTouches[0];
    if (t === undefined) return;
    endAimDrag(t.clientX, t.clientY);
    e.preventDefault();
  }

  // --- Mouse fallback (only if Pointer Events are unavailable) ---------------

  const hasPointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;

  function onMouseDown(e: MouseEvent): void {
    const phase = getState().swing.phase;
    if (phase === 'backswing' || phase === 'impact') {
      if (handleTapLock()) e.preventDefault();
      return;
    }
    if (phase !== 'aim') return;
    beginAimDrag(e.clientX, e.clientY, null);
    e.preventDefault();
  }

  function onMouseMove(e: MouseEvent): void {
    if (!dragging) return;
    moveAimDrag(e.clientX, e.clientY);
  }

  function onMouseUp(e: MouseEvent): void {
    if (!dragging) return;
    endAimDrag(e.clientX, e.clientY);
  }

  // --- Register ---------------------------------------------------------------

  const passiveFalse: AddEventListenerOptions = { passive: false };

  if (hasPointerEvents) {
    target.addEventListener('pointerdown', onPointerDown, passiveFalse);
    target.addEventListener('pointermove', onPointerMove, passiveFalse);
    // Listen on window for up/cancel so a release off the surface still ends the drag.
    window.addEventListener('pointerup', onPointerUp, passiveFalse);
    window.addEventListener('pointercancel', onPointerUp, passiveFalse);
  } else {
    target.addEventListener('touchstart', onTouchStart, passiveFalse);
    target.addEventListener('touchmove', onTouchMove, passiveFalse);
    window.addEventListener('touchend', onTouchEnd, passiveFalse);
    window.addEventListener('touchcancel', onTouchEnd, passiveFalse);
    target.addEventListener('mousedown', onMouseDown, passiveFalse);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  return function detach(): void {
    if (hasPointerEvents) {
      target.removeEventListener('pointerdown', onPointerDown);
      target.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    } else {
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      target.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
  };
}

// ---------------------------------------------------------------------------
// Legacy Phase-0 seam (kept until the overseer rewires app/main.ts)
// ---------------------------------------------------------------------------

/** @deprecated Phase 0 stub sink. Superseded by {@link ControlDeps.dispatch}. */
export type ActionSink = (action: Action) => void;

/**
 * @deprecated Phase 0 seam. `main.ts` (overseer-owned) still imports this; the
 * real control surface is {@link attachControls}. Kept so the current build stays
 * green until integration rewires the entry point. It now attaches the real
 * controls when given enough context; with only a sink it is inert (it cannot
 * read state or map coordinates), matching the old stub's no-op behaviour.
 */
export interface Controls {
  attach(): void;
  detach(): void;
}

/** @deprecated See {@link attachControls}. Inert with only a sink (no state/mapper). */
export function createControls(_canvas: HTMLCanvasElement, _emit: ActionSink): Controls {
  return {
    attach() {
      /* Inert: the real seam is attachControls(target, deps). */
    },
    detach() {
      /* no-op */
    },
  };
}
